// Campos adicionais do Assunto.
//
// Além do título, da descrição e dos campos fixos de §4.1, cada assunto pode
// pedir perguntas próprias. O formulário é uma lista ordenada de dois tipos de
// item:
//
//   campo — uma pergunta, respondida uma vez;
//   grupo — um conjunto de campos que o solicitante repete quantas vezes
//           precisar (o caso que motivou isto: "quais usuários devem ganhar
//           acesso", com nome, e-mail e perfil de cada um).
//
// Grupo não entra dentro de grupo. Dois níveis cobrem o que o cadastro precisa
// descrever e mantêm a tela de abertura legível.
//
// A resposta gravada no chamado leva junto o rótulo e o tipo do campo, pelo
// mesmo motivo que o SLA é congelado na abertura (§6.6): renomear ou apagar um
// campo do assunto não pode reescrever o que alguém respondeu meses atrás.
import { z } from 'zod';
export const SUBJECT_FIELD_TYPES = [
    'texto',
    'numero',
    'data',
    'data_hora',
    'hora',
    'opcao',
    'multipla_opcao',
    'sim_nao',
];
export const SUBJECT_FIELD_TYPE_LABEL = {
    texto: 'Texto',
    numero: 'Número',
    data: 'Data',
    data_hora: 'Data e hora',
    hora: 'Hora',
    opcao: 'Uma opção',
    multipla_opcao: 'Várias opções',
    sim_nao: 'Sim ou não',
};
/** Os dois tipos que precisam de opções cadastradas. */
export const SUBJECT_FIELD_OPTION_TYPES = ['opcao', 'multipla_opcao'];
export function usesOptions(type) {
    return SUBJECT_FIELD_OPTION_TYPES.includes(type);
}
/* ── O que o cadastro envia ──────────────────────────────────────────────── */
// O `id` viaja na entrada para o assunto conseguir ser salvo de novo sem
// trocar a identidade dos campos que já existiam: resposta antiga continua
// apontando para o campo certo. Id desconhecido é tratado como campo novo.
export const subjectFieldInputSchema = z.object({
    id: z.string().uuid().optional(),
    name: z.string().trim().min(2, 'Informe o nome do campo').max(120),
    hint: z.string().trim().max(240).optional().default(''),
    type: z.enum(SUBJECT_FIELD_TYPES),
    required: z.boolean().default(false),
    options: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
});
export const subjectFieldGroupInputSchema = z.object({
    id: z.string().uuid().optional(),
    name: z.string().trim().min(2, 'Informe o nome do grupo').max(120),
    hint: z.string().trim().max(240).optional().default(''),
    required: z.boolean().default(false),
    maxEntries: z.number().int().min(2).max(50).default(10),
    fields: z
        .array(subjectFieldInputSchema)
        .min(1, 'O grupo precisa de pelo menos um campo')
        .max(20),
});
export const subjectFormItemInputSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('campo'), field: subjectFieldInputSchema }),
    z.object({ kind: z.literal('grupo'), group: subjectFieldGroupInputSchema }),
]);
/** Erro de cadastro que o schema do item sozinho não enxerga. */
function checkFieldInput(field, ctx, path) {
    if (!usesOptions(field.type))
        return;
    if (field.options.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...path, 'options'],
            message: `Cadastre as opções de "${field.name}"`,
        });
        return;
    }
    if (new Set(field.options).size !== field.options.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...path, 'options'],
            message: `"${field.name}" tem opções repetidas`,
        });
    }
}
export const subjectFormInputSchema = z
    .array(subjectFormItemInputSchema)
    .max(40)
    .default([])
    .superRefine((items, ctx) => {
    items.forEach((item, i) => {
        if (item.kind === 'campo') {
            checkFieldInput(item.field, ctx, [i, 'field']);
            return;
        }
        item.group.fields.forEach((field, j) => {
            checkFieldInput(field, ctx, [i, 'group', 'fields', j]);
        });
    });
});
/* ── O que a abertura responde ───────────────────────────────────────────── */
export const subjectAnswerSchema = z.object({
    fieldId: z.string().uuid(),
    /** Qual repetição do grupo. Sempre 0 para campo fora de grupo. */
    entry: z.number().int().min(0).max(49).default(0),
    /** Vazio significa não respondido. Mais de um valor só em "Várias opções". */
    values: z.array(z.string().trim().max(2000)).max(50).default([]),
});
/* ── Validação ───────────────────────────────────────────────────────────── */
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const NUMBER_RE = /^-?\d+([.,]\d+)?$/;
function filled(values) {
    return values.map((v) => v.trim()).filter((v) => v.length > 0);
}
/**
 * Valida um valor contra o campo que o pediu. Devolve a mensagem pronta para a
 * tela ou `null` quando está tudo certo.
 *
 * É a mesma função nos dois lados: o formulário de abertura usa como regra do
 * Ant Design e a API usa antes de gravar.
 */
export function subjectFieldError(field, values) {
    const dados = filled(values);
    if (dados.length === 0) {
        if (!field.required)
            return null;
        return usesOptions(field.type) || field.type === 'sim_nao'
            ? `Escolha "${field.name}"`
            : `Informe "${field.name}"`;
    }
    if (field.type !== 'multipla_opcao' && dados.length > 1) {
        return `Responda "${field.name}" uma vez só`;
    }
    const primeiro = dados[0];
    switch (field.type) {
        case 'numero':
            if (!NUMBER_RE.test(primeiro))
                return `Use apenas números em "${field.name}"`;
            return null;
        case 'data': {
            // A volta por `toISOString` é o que pega 30 de fevereiro: o parser do
            // JavaScript aceita a data e empurra para março em silêncio.
            const dia = new Date(`${primeiro}T00:00:00Z`);
            if (!DATE_RE.test(primeiro) ||
                Number.isNaN(dia.getTime()) ||
                dia.toISOString().slice(0, 10) !== primeiro) {
                return `Informe uma data válida em "${field.name}"`;
            }
            return null;
        }
        case 'data_hora':
            if (Number.isNaN(Date.parse(primeiro))) {
                return `Informe uma data e hora válidas em "${field.name}"`;
            }
            return null;
        case 'hora':
            if (!TIME_RE.test(primeiro)) {
                return `Informe uma hora no formato 00:00 em "${field.name}"`;
            }
            return null;
        case 'sim_nao':
            if (primeiro !== 'true' && primeiro !== 'false') {
                return `Escolha "${field.name}"`;
            }
            return null;
        case 'opcao':
        case 'multipla_opcao': {
            const fora = dados.filter((v) => !field.options.includes(v));
            if (fora.length > 0)
                return `Escolha uma das opções de "${field.name}"`;
            return null;
        }
        default:
            return null;
    }
}
function indexAnswers(answers) {
    const index = new Map();
    for (const answer of answers) {
        const porEntrada = index.get(answer.fieldId) ?? new Map();
        const atual = porEntrada.get(answer.entry) ?? [];
        porEntrada.set(answer.entry, [...atual, ...answer.values]);
        index.set(answer.fieldId, porEntrada);
    }
    return index;
}
function valuesOf(index, fieldId, entry) {
    return index.get(fieldId)?.get(entry) ?? [];
}
export function resolveSubjectAnswers(form, answers) {
    const index = indexAnswers(answers);
    const values = [];
    const push = (field, groupLabel, entry, dados) => {
        for (const value of dados) {
            values.push({
                fieldId: field.id,
                groupLabel,
                entry,
                label: field.name,
                type: field.type,
                value,
                position: values.length,
            });
        }
    };
    for (const item of form) {
        if (item.kind === 'campo') {
            const dados = valuesOf(index, item.field.id, 0);
            const erro = subjectFieldError(item.field, dados);
            if (erro)
                return { ok: false, error: erro };
            push(item.field, '', 0, filled(dados));
            continue;
        }
        const group = item.group;
        const preenchidas = group.fields
            .flatMap((field) => [...(index.get(field.id)?.entries() ?? [])])
            .filter(([, dados]) => filled(dados).length > 0)
            .map(([entry]) => entry);
        const entradas = [...new Set(preenchidas)].sort((a, b) => a - b);
        if (group.required && entradas.length === 0) {
            return { ok: false, error: `Preencha pelo menos um item em "${group.name}"` };
        }
        if (entradas.length > group.maxEntries) {
            return {
                ok: false,
                error: `"${group.name}" aceita no máximo ${group.maxEntries} itens`,
            };
        }
        for (const [posicao, entrada] of entradas.entries()) {
            for (const field of group.fields) {
                const dados = valuesOf(index, field.id, entrada);
                const erro = subjectFieldError(field, dados);
                if (erro) {
                    return {
                        ok: false,
                        error: `${erro}, no item ${posicao + 1} de "${group.name}"`,
                    };
                }
                push(field, group.name, posicao, filled(dados));
            }
        }
    }
    return { ok: true, values };
}
/** Agrupa as linhas gravadas de volta em um valor por campo respondido. */
export function groupTicketFieldValues(rows) {
    const saida = [];
    for (const row of [...rows].sort((a, b) => a.position - b.position)) {
        const ultimo = saida[saida.length - 1];
        if (ultimo &&
            ultimo.label === row.label &&
            ultimo.groupLabel === row.groupLabel &&
            ultimo.entry === row.entry &&
            ultimo.type === row.type) {
            ultimo.values.push(row.value);
            continue;
        }
        saida.push({
            label: row.label,
            groupLabel: row.groupLabel,
            entry: row.entry,
            type: row.type,
            values: [row.value],
        });
    }
    return saida;
}
